import { sortByName, sortAsc } from '../utils/sortOptions';
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  PlusIcon, PencilSquareIcon, XMarkIcon,
  ChartBarIcon, Cog6ToothIcon, CheckCircleIcon,
} from '@heroicons/react/24/outline';

const DOMAINS      = ['cognitive','affective','psychomotor'];
const BLOOMS       = ['remember','understand','apply','analyze','evaluate','create'];
const BLOOMS_COLOR = { remember:'#6b7194', understand:'#3b6ef8', apply:'#10c98e', analyze:'#f59e0b', evaluate:'#ef4444', create:'#8b5cf6' };
const DOMAIN_COLOR = { cognitive:'#3b6ef8', affective:'#10c98e', psychomotor:'#f59e0b' };

const curYear = () => { const y=new Date().getFullYear(); return `${y}-${y+1}`; };

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .cm * { box-sizing:border-box; }
  .cm { font-family:'Sora',sans-serif; color:#1a1d2e; animation:cmFade .4s ease both; }
  @keyframes cmFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  .cm-hdr { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
  .cm-title { font-size:1.35rem; font-weight:700; color:#0f1123; letter-spacing:-.02em; }
  .cm-sub   { font-size:.76rem; color:#9094a8; margin-top:3px; }
  .tab-row { display:flex; gap:4px; background:#f0f2f7; border-radius:11px; padding:4px; margin-bottom:1.4rem; width:fit-content; flex-wrap:wrap; }
  .tab-btn { padding:7px 18px; border-radius:8px; border:none; font-family:'Sora',sans-serif; font-size:.78rem; font-weight:600; cursor:pointer; transition:all .15s; background:transparent; color:#9094a8; }
  .tab-btn.active { background:white; color:#0f1123; box-shadow:0 1px 4px rgba(0,0,0,.1); }
  .btn { display:inline-flex; align-items:center; gap:7px; padding:8px 16px; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; transition:all .18s; }
  .btn svg { width:15px; height:15px; stroke-width:2; }
  .btn-blue  { background:#3b6ef8; color:white; } .btn-blue:hover  { background:#2d5ef0; box-shadow:0 4px 14px rgba(59,110,248,.35); }
  .btn-ghost { background:#f0f2f7; color:#6b7194; } .btn-ghost:hover { background:#e4e7f0; }
  .btn-red   { background:#fef2f2; color:#ef4444; border:1px solid #fecaca; } .btn-red:hover { background:#ef4444; color:white; }
  .btn-sm { padding:5px 10px; font-size:.73rem; }
  .btn:disabled { opacity:.5; cursor:not-allowed; }
  .filters { display:flex; gap:10px; margin-bottom:1.2rem; flex-wrap:wrap; align-items:flex-end; }
  .fl { display:flex; flex-direction:column; gap:4px; }
  .fl-label { font-size:.67rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .fl-input,.fl-select { font-family:'Sora',sans-serif; font-size:.8rem; padding:8px 12px; border:1.5px solid #e0e4f0; border-radius:9px; background:white; outline:none; min-width:130px; }
  .fl-input:focus,.fl-select:focus { border-color:#3b6ef8; }
  .panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; }
  .panel-head { display:flex; align-items:center; justify-content:space-between; padding:14px 18px; border-bottom:1px solid #f0f2f8; flex-wrap:wrap; gap:8px; }
  .panel-title { font-size:.86rem; font-weight:600; color:#1a1d2e; }
  .clo-table { width:100%; border-collapse:collapse; }
  .clo-table thead { background:#fafbff; border-bottom:1px solid #edf0f9; }
  .clo-table th { padding:10px 14px; text-align:center !important; font-size:.65rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; white-space:nowrap; }
  .clo-table tbody tr { border-bottom:1px solid #f3f5fb; transition:background .12s; }
  .clo-table tbody tr:last-child { border-bottom:none; }
  .clo-table tbody tr:hover { background:#fafbff; }
  .clo-table td { padding:11px 14px; font-size:.8rem; vertical-align:middle; text-align:center !important; }
  .clo-code { font-family:'JetBrains Mono',monospace; font-size:.78rem; font-weight:700; color:#3b6ef8; background:#eef2ff; padding:3px 9px; border-radius:6px; display:inline-block; }
  .pill { display:inline-flex; font-size:.66rem; font-weight:600; padding:2px 9px; border-radius:99px; white-space:nowrap; text-transform:capitalize; }
  .cm-empty { text-align:center; padding:3rem 2rem; color:#9094a8; font-size:.82rem; }
  .cm-loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#3b6ef8; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg)} }
  /* OBE settings */
  .obe-toggle { display:flex; align-items:center; justify-content:space-between; padding:14px 18px; border-bottom:1px solid #f0f2f8; }
  .obe-toggle-title { font-weight:700; color:#0f1123; font-size:.84rem; }
  .obe-toggle-sub   { font-size:.72rem; color:#9094a8; margin-top:2px; }
  .toggle-switch { position:relative; width:44px; height:24px; flex-shrink:0; }
  .toggle-switch input { opacity:0; width:0; height:0; }
  .toggle-slider { position:absolute; inset:0; background:#e0e4f0; border-radius:99px; cursor:pointer; transition:.3s; }
  .toggle-slider::before { content:''; position:absolute; width:18px; height:18px; left:3px; bottom:3px; background:white; border-radius:50%; transition:.3s; box-shadow:0 1px 3px rgba(0,0,0,.2); }
  input:checked + .toggle-slider { background:#3b6ef8; }
  input:checked + .toggle-slider::before { transform:translateX(20px); }
  /* ── Modal ── */
  .cm-modal-backdrop {
    position:fixed;
    top:0; left:0;
    width:100vw; height:100vh;
    background:rgba(15,17,35,.65);
    backdrop-filter:blur(6px);
    -webkit-backdrop-filter:blur(6px);
    display:flex; align-items:center; justify-content:center;
    z-index:99999;
    overflow:hidden;
    box-sizing:border-box;
  }
  .cm-modal {
    background:white; border-radius:20px; padding:26px;
    width:520px; max-width:calc(100vw - 40px);
    box-shadow:0 24px 64px rgba(0,0,0,.3);
    animation:moIn .25s ease;
    position:relative;
    box-sizing:border-box;
    overflow:visible;
  }
  @keyframes moIn { from{opacity:0;transform:scale(.94)} to{opacity:1;transform:scale(1)} }
  .cm-modal-head { display:flex; align-items:center; justify-content:space-between; margin-bottom:18px; }
  .cm-modal-title { font-size:1rem; font-weight:700; color:#0f1123; }
  .cm-modal-close { width:30px; height:30px; border-radius:8px; border:none; background:#f0f2f7; cursor:pointer; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
  .cm-modal-close:hover { background:#e4e7f0; }
  .cm-modal-close svg { width:14px; height:14px; stroke-width:2; }
  .cm-modal-grid { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
  .mf { display:flex; flex-direction:column; gap:5px; }
  .mf.full { grid-column:1/-1; }
  .mf-label { font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .mf-input,.mf-select,.mf-textarea { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%; }
  .mf-input:focus,.mf-select:focus,.mf-textarea:focus { border-color:#3b6ef8; background:white; }
  .mf-textarea { resize:vertical; min-height:52px; }
  .mf-hint { font-size:.67rem; color:#b0b5cc; }
  .cm-modal-btns { display:flex; gap:8px; justify-content:flex-end; margin-top:18px; flex-wrap:wrap; }
  /* Mobile cards for CLO table */
  .cm-cards { display:none; padding:10px; }
  .cm-mcard { background:white; border-radius:12px; border:1px solid #e8ebf4; padding:13px; margin-bottom:8px; }
  .cm-mc-top { display:flex; align-items:center; justify-content:space-between; gap:8px; flex-wrap:wrap; margin-bottom:8px; }
  .cm-mc-desc { font-size:.78rem; color:#1a1d2e; line-height:1.45; margin-bottom:10px; }
  .cm-mc-row { display:flex; justify-content:space-between; align-items:center; font-size:.76rem; padding:5px 0; border-bottom:1px solid #f0f2f8; gap:8px; }
  .cm-mc-row:last-of-type { border-bottom:none; }
  .cm-mc-label { color:#9094a8; font-weight:600; white-space:nowrap; }
  .cm-mc-actions { display:flex; gap:6px; margin-top:10px; }
  @media (max-width:600px) {
    .cm-title { font-size:1.1rem; }
    .filters { flex-direction:column; }
    .fl-input,.fl-select { width:100%; min-width:unset; }
    .cm-modal-grid { grid-template-columns:1fr; }
    .mf.full { grid-column:1; }
    .modal { padding:18px; border-radius:16px; }
    .cm-modal-btns { justify-content:stretch; }
    .modal-btns .btn { flex:1; justify-content:center; }
  }
  @media (max-width:768px) {
    .cm-tbl-wrap { display:none !important; }
    .cm-cards { display:block; }
  }
`;

export default function CLOManagement() {
  const [tab,          setTab]         = useState('clos');
  const [clos,         setClos]        = useState([]);
  const [classes,      setClasses]     = useState([]);
  const [loading,      setLoading]     = useState(false);

  // Filter state
  const [filterClass,  setFilterClass] = useState('');
  const [filterSubj,   setFilterSubj]  = useState('');
  const [filterYear,   setFilterYear]  = useState(curYear());
  const [filterSection,setFilterSection]=useState('');

  // Modal state
  const [modal,        setModal]       = useState(null); // null | 'add' | clo object
  const [form,         setForm]        = useState({
    classId:'', section:'', subject:'', academicYear:curYear(),
    code:'', description:'', domain:'cognitive', bloomsLevel:'understand', threshold:''
  });
  const [saving,       setSaving]      = useState(false);

  // Lock html + body scroll when modal is open
  useEffect(() => {
    if (modal) {
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    } else {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    }
    return () => {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    };
  }, [modal]);

  // OBE settings
  const [obeSettings,  setObeSettings] = useState({ obeEnabled:false, obeThreshold:60, obeTeacherEnabled:true, obeCoordEnabled:true });
  const [savingObe,    setSavingObe]   = useState(false);

  useEffect(() => {
    api.get('/admin/classes').then(r => setClasses(r.data?.classes||r.data||[])).catch(()=>{});
    fetchObeSettings();
  }, []);

  useEffect(() => { if (tab==='clos') fetchCLOs(); }, [filterClass, filterSection, filterSubj, filterYear, tab]);

  const fetchCLOs = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (filterClass)   p.append('classId',      filterClass);
      if (filterSubj)    p.append('subject',       filterSubj);
      if (filterYear)    p.append('academicYear',  filterYear);
      if (filterSection) p.append('section',       filterSection);
      const { data } = await api.get(`/admin/clos?${p}`);
      const list = Array.isArray(data) ? data : [];
      setClos(list);
    } catch { toast.error('Failed to load CLOs'); }
    finally { setLoading(false); }
  };

  const fetchObeSettings = async () => {
    try { const { data } = await api.get('/admin/clos/settings'); setObeSettings(data); }
    catch {}
  };

  const saveObeSettings = async () => {
    setSavingObe(true);
    try { await api.put('/admin/clos/settings', obeSettings); toast.success('OBE settings saved'); }
    catch { toast.error('Failed'); }
    finally { setSavingObe(false); }
  };

  // Derived: subjects for the selected class
  const classSubjects = (classId) => {
    const cls = classes.find(c => String(c._id) === String(classId));
    if (!cls?.subjects) return [];
    return [...new Set(
      (Array.isArray(cls.subjects) ? cls.subjects : [cls.subjects])
        .flatMap(s => String(s).split(',').map(x => x.trim()))
        .filter(Boolean)
    )].sort();
  };

  const classSections = (classId) =>
    classes.find(c => String(c._id) === String(classId))?.sections || [];

  // Filter bar subjects (from loaded CLOs or selected class)
  // Always derive subjects from class when one is selected
  // Fall back to subjects seen in loaded CLOs only when no class filter
  const filterSubjects = filterClass
    ? classSubjects(filterClass)
    : [...new Set(clos.map(c => c.subject).filter(Boolean))].sort();

  const filterSections = filterClass ? classSections(filterClass) : [];

  const openAdd = () => {
    setForm({
      classId:     filterClass||'',
      section:     filterSection||'',
      subject:     filterSubj||'',
      academicYear:filterYear||curYear(),
      code:'', description:'', domain:'cognitive', bloomsLevel:'understand', threshold:''
    });
    setModal('add');
  };

  const openEdit = (clo) => {
    setForm({
      classId:     clo.class?._id||'',
      section:     clo.section||'',
      subject:     clo.subject,
      academicYear:clo.academicYear,
      code:        clo.code,
      description: clo.description,
      domain:      clo.domain,
      bloomsLevel: clo.bloomsLevel,
      threshold:   clo.threshold??'',
    });
    setModal(clo);
  };

  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.classId||!form.subject||!form.academicYear||!form.code||!form.description)
      return toast.error('Class, subject, year, code and description are required');
    setSaving(true);
    try {
      if (modal === 'add') {
        await api.post('/admin/clos', {
          classId:     form.classId,
          section:     form.section||'',
          subject:     form.subject,
          academicYear:form.academicYear,
          code:        form.code,
          description: form.description,
          domain:      form.domain,
          bloomsLevel: form.bloomsLevel,
          threshold:   form.threshold||null,
        });
        toast.success('CLO created');
      } else {
        await api.put(`/admin/clos/${modal._id}`, {
          description: form.description,
          domain:      form.domain,
          bloomsLevel: form.bloomsLevel,
          threshold:   form.threshold||null,
        });
        toast.success('CLO updated');
      }
      setModal(null);
      fetchCLOs();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  // Modal form subjects/sections based on selected class
  const modalSubjects = classSubjects(form.classId);
  const modalSections = classSections(form.classId);

  return (
    <div className="cm"><style>{css}</style>

      <div className="cm-hdr">
        <div>
          <div className="cm-title">OBE — CLO Management</div>
          <div className="cm-sub">Define Course Learning Outcomes per subject, class and academic year</div>
        </div>
        {tab==='clos' && <button className="btn btn-blue" onClick={openAdd}><PlusIcon />Add CLO</button>}
      </div>

      <div className="tab-row">
        <button className={`tab-btn ${tab==='clos'?'active':''}`}    onClick={()=>setTab('clos')}>CLOs</button>
        <button className={`tab-btn ${tab==='settings'?'active':''}`} onClick={()=>setTab('settings')}>
          <Cog6ToothIcon style={{width:13,height:13,display:'inline',marginRight:4}}/>OBE Settings
        </button>
      </div>

      {/* ── OBE Settings ── */}
      {tab==='settings' && (
        <div className="panel">
          {[
            { key:'obeEnabled',        title:'Enable OBE',                      sub:'Turn on Outcome Based Education. Shows CLO mapping in assessments and attainment reports.' },
            { key:'obeTeacherEnabled', title:'Teachers can view attainment',     sub:'Allow teachers to see CLO attainment reports for their classes.' },
            { key:'obeCoordEnabled',   title:'Coordinators can view attainment', sub:'Allow coordinators to view CLO attainment dashboards.' },
          ].map(item => (
            <div key={item.key} className="obe-toggle">
              <div>
                <div className="obe-toggle-title">{item.title}</div>
                <div className="obe-toggle-sub">{item.sub}</div>
              </div>
              <label className="toggle-switch">
                <input type="checkbox" checked={!!obeSettings[item.key]}
                  onChange={e => setObeSettings(p => ({...p,[item.key]:e.target.checked}))} />
                <span className="toggle-slider"/>
              </label>
            </div>
          ))}
          <div style={{padding:'14px 18px',display:'flex',alignItems:'center',gap:14,flexWrap:'wrap',borderBottom:'1px solid #f0f2f8'}}>
            <div style={{flex:1}}>
              <div style={{fontWeight:700,fontSize:'.84rem',color:'#0f1123'}}>Default Attainment Threshold</div>
              <div style={{fontSize:'.72rem',color:'#9094a8',marginTop:2}}>% of students that must achieve a CLO for it to be considered attained</div>
            </div>
            <div style={{display:'flex',alignItems:'center',gap:8}}>
              <input type="number" min={0} max={100}
                style={{width:70,fontFamily:"'JetBrains Mono',monospace",fontSize:'.9rem',padding:'6px 10px',border:'1.5px solid #e0e4f0',borderRadius:9,background:'#f8f9fc',outline:'none',textAlign:'center'}}
                value={obeSettings.obeThreshold}
                onChange={e => setObeSettings(p => ({...p,obeThreshold:Number(e.target.value)}))} />
              <span style={{fontSize:'.82rem',color:'#6b7194',fontWeight:600}}>%</span>
            </div>
          </div>
          <div style={{padding:'14px 18px'}}>
            <button className="btn btn-blue" onClick={saveObeSettings} disabled={savingObe}>
              <CheckCircleIcon />{savingObe?'Saving…':'Save OBE Settings'}
            </button>
          </div>
        </div>
      )}

      {/* ── CLOs tab ── */}
      {tab==='clos' && (<>
        <div className="filters">
          {/* Class */}
          <div className="fl">
            <label className="fl-label">Class</label>
            <select className="fl-select" value={filterClass}
              onChange={e => { setFilterClass(e.target.value); setFilterSection(''); setFilterSubj(''); }}>
              <option value="">All Classes</option>
              {sortByName(classes).map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
            </select>
          </div>
          {/* Section — only when class selected */}
          <div className="fl">
            <label className="fl-label">Section</label>
            <select className="fl-select" value={filterSection}
              onChange={e => setFilterSection(e.target.value)}
              disabled={!filterClass || filterSections.length===0}>
              <option value="">{filterClass ? 'All Sections' : '—'}</option>
              {sortAsc(filterSections).map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          {/* Subject — auto-fetched from class */}
          <div className="fl">
            <label className="fl-label">Subject</label>
            {filterClass ? (
              <select className="fl-select" value={filterSubj} onChange={e => setFilterSubj(e.target.value)}>
                <option value="">All Subjects</option>
                {sortAsc(filterSubjects).map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            ) : (
              <input className="fl-input" placeholder="Type subject…" value={filterSubj}
                onChange={e => setFilterSubj(e.target.value)} />
            )}
          </div>
          {/* Year */}
          <div className="fl">
            <label className="fl-label">Academic Year</label>
            <input className="fl-input" placeholder="2025-2026" value={filterYear}
              onChange={e => setFilterYear(e.target.value)} style={{minWidth:110}} />
          </div>
          {(filterClass||filterSubj||filterSection) && (
            <button className="btn btn-ghost btn-sm" style={{alignSelf:'flex-end'}}
              onClick={() => { setFilterClass(''); setFilterSection(''); setFilterSubj(''); }}>
              Clear
            </button>
          )}
        </div>

        <div className="panel">
          <div className="panel-head">
            <div className="panel-title">CLOs ({clos.length})</div>
          </div>
          {loading ? (
            <div className="cm-loading"><div className="spinner"/>Loading…</div>
          ) : clos.length === 0 ? (
            <div className="cm-empty">
              No CLOs defined yet.{' '}
              <button onClick={openAdd} style={{color:'#3b6ef8',background:'none',border:'none',cursor:'pointer',fontWeight:600,fontFamily:'Sora',fontSize:'.82rem'}}>
                Add first CLO →
              </button>
            </div>
          ) : (
            <>
              <div className="cm-tbl-wrap" style={{overflowX:'auto'}}>
                <table className="clo-table">
                  <thead><tr>
                    <th>Code</th><th>Description</th><th>Class</th>
                    <th>Section</th><th>Subject</th><th>Year</th>
                    <th>Domain</th><th>Bloom's</th><th>Threshold</th><th>Actions</th>
                  </tr></thead>
                  <tbody>
                    {clos.map(clo => (
                      <tr key={clo._id}>
                        <td><span className="clo-code">{clo.code}</span></td>
                        <td style={{maxWidth:240,fontSize:'.78rem',color:'#1a1d2e'}}>{clo.description}</td>
                        <td style={{fontSize:'.76rem',fontWeight:600}}>{clo.class?.name}</td>
                        <td style={{fontSize:'.74rem',color:'#6b7194'}}>{clo.section||<span style={{color:'#c0c5dc'}}>All</span>}</td>
                        <td style={{fontSize:'.76rem',color:'#6b7194'}}>{clo.subject}</td>
                        <td style={{fontFamily:"'JetBrains Mono',monospace",fontSize:'.74rem',color:'#9094a8'}}>{clo.academicYear}</td>
                        <td><span className="pill" style={{background:`${DOMAIN_COLOR[clo.domain]}18`,color:DOMAIN_COLOR[clo.domain]}}>{clo.domain}</span></td>
                        <td><span className="pill" style={{background:`${BLOOMS_COLOR[clo.bloomsLevel]}18`,color:BLOOMS_COLOR[clo.bloomsLevel]}}>{clo.bloomsLevel}</span></td>
                        <td style={{fontFamily:"'JetBrains Mono',monospace",fontSize:'.78rem',fontWeight:600,color:clo.threshold!=null?'#3b6ef8':'#c0c5dc'}}>
                          {clo.threshold!=null?`${clo.threshold}%`:'default'}
                        </td>
                        <td>
                          <div style={{display:'flex',gap:5}}>
                            <button className="btn btn-ghost btn-sm" onClick={()=>openEdit(clo)}><PencilSquareIcon />Edit</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="cm-cards">
                {clos.map(clo => (
                  <div key={clo._id} className="cm-mcard">
                    <div className="cm-mc-top">
                      <span className="clo-code">{clo.code}</span>
                      <div style={{display:'flex',gap:5,flexWrap:'wrap'}}>
                        <span className="pill" style={{background:`${DOMAIN_COLOR[clo.domain]}18`,color:DOMAIN_COLOR[clo.domain]}}>{clo.domain}</span>
                        <span className="pill" style={{background:`${BLOOMS_COLOR[clo.bloomsLevel]}18`,color:BLOOMS_COLOR[clo.bloomsLevel]}}>{clo.bloomsLevel}</span>
                      </div>
                    </div>
                    <div className="cm-mc-desc">{clo.description}</div>
                    <div className="cm-mc-row"><span className="cm-mc-label">Class</span><span style={{fontWeight:600}}>{clo.class?.name}</span></div>
                    <div className="cm-mc-row"><span className="cm-mc-label">Section</span><span>{clo.section || 'All'}</span></div>
                    <div className="cm-mc-row"><span className="cm-mc-label">Subject</span><span>{clo.subject}</span></div>
                    <div className="cm-mc-row"><span className="cm-mc-label">Academic Year</span><span style={{fontFamily:"'JetBrains Mono',monospace"}}>{clo.academicYear}</span></div>
                    <div className="cm-mc-row">
                      <span className="cm-mc-label">Threshold</span>
                      <span style={{fontFamily:"'JetBrains Mono',monospace",fontWeight:600,color:clo.threshold!=null?'#3b6ef8':'#c0c5dc'}}>
                        {clo.threshold!=null?`${clo.threshold}%`:'default'}
                      </span>
                    </div>
                    <div className="cm-mc-actions">
                      <button className="btn btn-ghost btn-sm" onClick={()=>openEdit(clo)}><PencilSquareIcon />Edit</button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </>)}

      {/* ── Add/Edit Modal ── */}
      {modal && createPortal(
        <div className="cm-modal-backdrop" onClick={()=>setModal(null)}>
          <div className="cm-modal" onClick={e=>e.stopPropagation()}>
            <div className="cm-modal-head">
              <div className="cm-modal-title">{modal==='add'?'Add CLO':`Edit ${modal.code}`}</div>
              <button className="cm-modal-close" onClick={()=>setModal(null)}><XMarkIcon /></button>
            </div>

            <div className="cm-modal-grid">
              {/* ── Add-only fields: class, year, subject, section, code ── */}
              {modal==='add' && (<>
                <div className="mf">
                  <label className="mf-label">Class *</label>
                  <select className="mf-select" value={form.classId}
                    onChange={e => { setF('classId',e.target.value); setF('section',''); setF('subject',''); }}>
                    <option value="">Select class…</option>
                    {classes.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                  </select>
                </div>
                <div className="mf">
                  <label className="mf-label">Academic Year *</label>
                  <input className="mf-input" placeholder="2025-2026" value={form.academicYear}
                    onChange={e=>setF('academicYear',e.target.value)} />
                </div>
                {/* Section — auto-fetched from class */}
                <div className="mf">
                  <label className="mf-label">
                    Section
                    <span style={{fontWeight:400,color:'#b0b5cc',textTransform:'none',marginLeft:5}}>(optional — blank = all sections)</span>
                  </label>
                  <select className="mf-select" value={form.section}
                    onChange={e=>setF('section',e.target.value)}
                    disabled={!form.classId || modalSections.length===0}>
                    <option value="">All Sections</option>
                    {modalSections.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                {/* Subject — auto-fetched from class */}
                <div className="mf">
                  <label className="mf-label">Subject *</label>
                  {form.classId && modalSubjects.length > 0 ? (
                    <select className="mf-select" value={form.subject} onChange={e=>setF('subject',e.target.value)}>
                      <option value="">Select subject…</option>
                      {modalSubjects.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  ) : (
                    <input className="mf-input" placeholder="e.g. Mathematics" value={form.subject}
                      onChange={e=>setF('subject',e.target.value)} />
                  )}
                </div>
                <div className="mf full">
                  <label className="mf-label">CLO Code *</label>
                  <input className="mf-input" placeholder="e.g. CLO-1" value={form.code}
                    onChange={e=>setF('code',e.target.value)}
                    style={{fontFamily:"'JetBrains Mono',monospace"}} />
                  <span className="mf-hint">Must be unique per subject / class / year</span>
                </div>
              </>)}

              {/* Description — always shown */}
              <div className="mf full">
                <label className="mf-label">Description *</label>
                <textarea className="mf-textarea" placeholder="Students will be able to…"
                  value={form.description} onChange={e=>setF('description',e.target.value)} />
              </div>

              <div className="mf">
                <label className="mf-label">Domain</label>
                <select className="mf-select" value={form.domain} onChange={e=>setF('domain',e.target.value)}>
                  {DOMAINS.map(d => <option key={d} value={d}>{d.charAt(0).toUpperCase()+d.slice(1)}</option>)}
                </select>
              </div>
              <div className="mf">
                <label className="mf-label">Bloom's Level</label>
                <select className="mf-select" value={form.bloomsLevel} onChange={e=>setF('bloomsLevel',e.target.value)}>
                  {BLOOMS.map(b => <option key={b} value={b}>{b.charAt(0).toUpperCase()+b.slice(1)}</option>)}
                </select>
              </div>
              <div className="mf full">
                <label className="mf-label">
                  Custom Threshold %
                  <span style={{fontWeight:400,color:'#b0b5cc',marginLeft:5}}>
                    (leave blank to use school default)
                  </span>
                </label>
                <input type="number" min={0} max={100} className="mf-input"
                  placeholder={`e.g. ${obeSettings.obeThreshold||60}`}
                  value={form.threshold} onChange={e=>setF('threshold',e.target.value)}
                  style={{maxWidth:130,fontFamily:"'JetBrains Mono',monospace"}} />
              </div>
            </div>

            <div className="cm-modal-btns">
              <button className="btn btn-ghost" onClick={()=>setModal(null)}>Cancel</button>
              <button className="btn btn-blue" onClick={handleSave} disabled={saving}>
                {saving?'Saving…':'Save CLO'}
              </button>
            </div>
          </div>
        </div>
      , document.body)}
    </div>
  );
}